import { useMemo, useState } from "react";
import OsmPoiClassificationList from "./OsmPoiClassificationList";
import Classifications, { Classification } from "./OsmPoiClassifications";
import { useFormikContext } from "formik";
import { Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";
import { OsmPoiContext } from "./OsmPoiContext";

const OsmPoiClassificationsField = () => {
  const { values, setValues } = useFormikContext<{
    classifications: number[];
  }>();
  const [expanded, setExpanded] = useState<{ classes: number[] }>({
    classes: [],
  });

  // Toggles the selected class
  const toggleClass = (classification: Classification) => {
    if (isSelfOrAncestorsSelected(classification)) {
      removeClass(classification);

      // Trim the expanded class tree
      expanded.classes.splice(classification.Level!);
    } else {
      addClass(classification);

      // If there are sub-classes, trim the expanded class tree
      // and expand this category
      if (classification.Subclasses) {
        expanded.classes.splice(classification.Level!);
        expanded.classes.push(classification.Code);
      }
    }

    setValues({ ...values });
    setExpanded({ classes: expanded.classes });
  };

  const expandClass = (classification: Classification) => {
    expanded.classes.splice(classification.Level!);
    expanded.classes.push(classification.Code);
    setExpanded({ classes: expanded.classes });
  };

  // Returns true if the class is selected or its ancestor is selected
  const isSelfOrAncestorsSelected = (classification: Classification) => {
    if (isSelected(classification)) return true;

    let parent = classification.Parent;
    while (parent) {
      if (isSelected(parent)) return true;

      parent = parent.Parent;
    }

    return false;
  };

  // Returns true of the class is selected
  const isSelected = (classification: Classification) => {
    return values.classifications.some((c) => c === classification.Code);
  };

  // Returns true if all sub-classes of a class are selected
  const allChildrenSelected = (classification: Classification) => {
    if (!classification.Subclasses) return false;
    return classification.Subclasses.every((subClass) => isSelected(subClass));
  };

  // Returns true if any decentant classes of a class is selected
  const anyDecendentSelected = (classification: Classification) => {
    if (!classification.Subclasses) return false;

    for (let subClass of classification.Subclasses) {
      if (isSelected(subClass)) return true;
      if (anyDecendentSelected(subClass)) return true;
    }

    return false;
  };

  // Gets a list of the selected classifications
  const findSelectedClassifications = (
    classifications: Classification[],
  ): Classification[] => {
    const selected = [];
    for (let classification of classifications) {
      if (values.classifications.some((c) => c === classification.Code)) {
        selected.push(classification);
      }

      if (classification.Subclasses) {
        selected.push(
          ...findSelectedClassifications(classification.Subclasses),
        );
      }
    }

    return selected;
  };

  // Adds a class and adjusts the tree
  const addClass = (classification: Classification) => {
    removeClassRecursive(classification); // Clear it's children, if any
    addClassInternal(classification);

    // Travelling up the tree; If all subclasses of a class
    // are selected, deselect them and select their parent
    // class instead
    let parent = classification.Parent;
    while (parent) {
      if (allChildrenSelected(parent)) {
        removeClassRecursive(parent); // Recursively removes the children
        addClassInternal(parent);
      }

      parent = parent.Parent;
    }
  };

  // Removes a class and adjusts the tree
  const removeClass = (classification: Classification) => {
    removeClassRecursive(classification);

    // Travelling up the tree; If the parent is selected,
    // instead select all the other siblings and de-select
    // the parent
    let currentClass = classification;
    while (currentClass.Parent) {
      if (isSelfOrAncestorsSelected(currentClass.Parent)) {
        removeClassInternal(currentClass.Parent);
        const siblings = currentClass.Parent.Subclasses!.filter(
          (subClass) => subClass.Code !== currentClass.Code,
        );
        addClassesInternal(siblings);
      }

      currentClass = currentClass.Parent;
    }
  };

  // Recursively removes a class and its decendents from the form
  // value without any other processing
  const removeClassRecursive = (classification: Classification) => {
    removeClassInternal(classification);
    if (classification.Subclasses)
      classification.Subclasses.forEach((subClass) =>
        removeClassRecursive(subClass),
      );
  };

  // Removes a class from the form value without any other processing
  const removeClassInternal = (classification: Classification) => {
    const index = values.classifications.indexOf(classification.Code, 0);
    if (index > -1) values.classifications.splice(index, 1);
  };

  // Adds a class to the form value without any other processing
  const addClassInternal = (classification: Classification) => {
    values.classifications.push(classification.Code);
  };

  // Adds a class to the form value without any other processing
  const addClassesInternal = (classifications: Classification[]) => {
    values.classifications.push(...classifications.map((c) => c.Code));
  };

  // Sorting function for classifications
  const sort = (a: Classification, b: Classification): number => {
    if (a.Subclasses && !b.Subclasses) return -1;
    if (!a.Subclasses && b.Subclasses) return 1;
    return a.Name.localeCompare(b.Name);
  };

  const reset = () => {
    setValues({ ...values, classifications: [] });
    setExpanded({ classes: [] });
  };

  const selectedClassifications: Classification[] = useMemo(() => {
    return findSelectedClassifications(Classifications).sort(sort);
  }, [values]);

  return (
    <OsmPoiContext.Provider
      value={{
        expanded: expanded,
        toggleClass: toggleClass,
        expandClass: expandClass,
        isSelfOrAncestorsSelected: isSelfOrAncestorsSelected,
        anyDecendentSelected: anyDecendentSelected,
        sort: sort,
      }}>
      <div className="d-flex overflow-auto flex-nowrap pb-2">
        <OsmPoiClassificationList
          classifications={Classifications}
          title="Categories"
        />
      </div>
      <div className="d-flex align-items-start">
        <Button
          style={{ width: 140 }}
          className="flex-shrink-0"
          onClick={() => reset()}>
          Reset Selection
        </Button>
        <div
          className="ms-2 d-flex flex-wrap 
        gap-2">
          {selectedClassifications.map((classification) => (
            <Button
              key={classification.Code}
              variant="outline-info"
              size="sm"
              style={{ textAlign: "left" }}
              onClick={() => {
                toggleClass(classification);
              }}>
              {classification.Subclasses && <b>{classification.Name}</b>}
              {!classification.Subclasses && classification.Name}
            </Button>
          ))}
        </div>
      </div>
    </OsmPoiContext.Provider>
  );
};

export default OsmPoiClassificationsField;
